/**
 * Admin Authentication Service
 *
 * Handles authentication specifically for admin users with role-based access control
 */

import { apiService } from './api'
import { clearStoredSession, ADMIN_HOSTNAME } from './sessionStorageKeys'

export interface AdminUser {
  id: string
  email: string
  name: string
  role: 'admin'
  permissions: string[]
  createdAt: string
}

export interface AdminAuthResponse {
  user: AdminUser
  token: string
  expiresIn: number
}

export interface AdminLoginData {
  email: string
  password: string
}

class AdminAuthService {
  private adminToken: string | null = null
  private adminUser: AdminUser | null = null

  constructor() {
    this.loadAdminFromStorage()
  }

  private loadAdminFromStorage(): void {
    try {
      this.adminToken = localStorage.getItem('admin_token')
      const userData = localStorage.getItem('admin_user')
      if (userData) {
        this.adminUser = JSON.parse(userData)
      }
    } catch (error) {
      console.warn('Failed to load admin data from storage:', error)
      this.clearAdminData()
    }
  }

  private storeAdminData(user: AdminUser, token: string): void {
    try {
      this.adminToken = token
      this.adminUser = user
      localStorage.setItem('admin_token', token)
      localStorage.setItem('admin_user', JSON.stringify(user))
    } catch (error) {
      console.warn('Failed to store admin data:', error)
    }
  }

  private clearAdminData(): void {
    this.adminToken = null
    this.adminUser = null
    // 지울 키 목록은 sessionStorageKeys 한 곳에서 정한다
    clearStoredSession(ADMIN_HOSTNAME)
  }

  /**
   * Login admin user
   */
  async login(data: AdminLoginData): Promise<AdminAuthResponse> {
    try {
      const response = await apiService.client.post<{
        success: boolean
        data: AdminAuthResponse
      }>('/admin/login', data)

      if (response.data.success && response.data.data) {
        const { user, token } = response.data.data

        // Verify user has admin role
        if (user.role !== 'admin') {
          throw new Error('Access denied: Admin privileges required')
        }

        this.storeAdminData(user, token)
        return response.data.data
      } else {
        throw new Error('Login failed')
      }
    } catch (error: any) {
      this.clearAdminData()
      throw error
    }
  }

  /**
   * Logout admin user
   */
  async logout(): Promise<void> {
    try {
      if (this.adminToken) {
        await apiService.client.post(
          '/admin/logout',
          {},
          {
            headers: {
              Authorization: `Bearer ${this.adminToken}`,
            },
          }
        )
      }
    } catch (error) {
      console.warn('Logout API call failed:', error)
    } finally {
      this.clearAdminData()
    }
  }

  /**
   * Check if user is authenticated as admin
   */
  isAuthenticated(): boolean {
    return !!(
      this.adminToken &&
      this.adminUser &&
      this.adminUser.role === 'admin'
    )
  }

  /**
   * Get current admin user
   */
  getCurrentUser(): AdminUser | null {
    return this.adminUser
  }

  /**
   * Get admin auth token
   */
  getToken(): string | null {
    return this.adminToken
  }

  /**
   * Verify admin token with server
   */
  async verifyToken(): Promise<boolean> {
    try {
      if (!this.adminToken) {
        return false
      }

      const response = await apiService.client.get('/admin/verify', {
        headers: {
          Authorization: `Bearer ${this.adminToken}`,
        },
      })

      return response.data.success
    } catch (error) {
      console.warn('Token verification failed:', error)
      this.clearAdminData()
      return false
    }
  }

  /**
   * Check if admin has specific permission
   */
  hasPermission(permission: string): boolean {
    if (!this.adminUser) {
      return false
    }
    return (
      this.adminUser.permissions.includes(permission) ||
      this.adminUser.permissions.includes('*')
    )
  }

  /**
   * Refresh admin data
   */
  async refreshUser(): Promise<AdminUser | null> {
    try {
      if (!this.adminToken) {
        return null
      }

      const response = await apiService.client.get('/admin/profile', {
        headers: {
          Authorization: `Bearer ${this.adminToken}`,
        },
      })

      if (response.data.success && response.data.data) {
        const user = response.data.data
        this.adminUser = user
        localStorage.setItem('admin_user', JSON.stringify(user))
        return user
      }

      return null
    } catch (error) {
      console.warn('Failed to refresh admin user:', error)
      this.clearAdminData()
      return null
    }
  }
}

// Create and export singleton instance
export const adminAuthService = new AdminAuthService()
